import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { PageSection } from "@/components/site/PageSection";
import { TelehealthCta } from "@/components/site/TelehealthCta";
import {
  Callout,
  CurrencyNote,
  FaqList,
  H2,
  H3,
  InsuranceCTA,
  P,
  SITE,
  SpokeCards,
  UL,
  faqSchema,
  medicalWebPageSchema,
  type Faq,
} from "@/components/site/InsuranceProse";

const TITLE =
  "Medicaid & COVA GLP-1 Coverage in VA | Virginia Bariatric";
const DESCRIPTION =
  "Cardinal Care's BMI 40/37 rules and failed-trial definitions, plus the July 2026 COVA change requiring a BMI of 35 for weight-loss GLP-1 coverage.";
const URL = `${SITE}/medical-weight-loss/insurance/medicaid-cova`;
const NAME = "Virginia Medicaid and State Employee GLP-1 Coverage";

const FAQS: Faq[] = [
  {
    q: "What BMI does Virginia Medicaid require for weight-loss medication?",
    a: "Virginia Medicaid (Cardinal Care) has the strictest criteria of any major payor in the state: BMI over 40, or BMI over 37 with high cholesterol, high blood pressure, or type 2 diabetes. You must also have tried and failed a non-GLP-1 weight-loss medication and the plan's preferred product, and your provider must attest that your obesity is disabling and life threatening.",
  },
  {
    q: "Did Virginia state employee coverage change?",
    a: "Yes. Effective July 1, 2026, the Virginia state employee plans (COVA) require a BMI of 35 or higher for weight-loss GLP-1 coverage, up from the previous 27/30 standard. Current users are not grandfathered — existing patients become subject to the new criteria once their prior authorization expires.",
  },
];

export const Route = createFileRoute("/medical-weight-loss/insurance/medicaid-cova")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: URL },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates("/medical-weight-loss/insurance/medicaid-cova") : []),
    ],
    scripts: import.meta.env.SSR ? [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS, URL)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          medicalWebPageSchema({ url: URL, name: NAME, description: DESCRIPTION })
        ),
      },
    ] : [],
  }),
  component: MedicaidCovaPage,
});

function MedicaidCovaPage() {
  return (
    <>
      <PageSection className="pt-8 pb-4 text-center">
        <h1 className="text-3xl md:text-5xl font-serif">
          Virginia Medicaid and State Employee Coverage
        </h1>
        <p className="mt-4 max-w-3xl mx-auto text-muted-foreground leading-[1.6]">
          Cardinal Care and the COVA plans have Virginia&rsquo;s strictest weight-loss medication
          rules &mdash; and both changed on July 1, 2026.
        </p>
        <TelehealthCta />
      </PageSection>

      <PageSection maxWidth="4xl" className="pb-4">
        <CurrencyNote />
        <P>
          New here? Start with the{" "}
          <Link
            to="/medical-weight-loss/insurance"
            preload="intent"
            className="text-brand-deep underline hover:no-underline"
          >
            coverage overview
          </Link>{" "}
          to understand the two gates and the diagnosis backdoor before reading the plan rules
          below.
        </P>

        <H2 id="medicaid">Virginia Medicaid (Cardinal Care)</H2>
        <P>
          Virginia Medicaid covers these drugs, but with the strictest criteria of any major payor
          in the state. The rules below come from the Commonwealth&rsquo;s official Weight-Loss
          Management authorization form, effective July 1, 2026, and apply to fee-for-service
          members and to managed care plans including Anthem HealthKeepers Plus, Aetna Better
          Health, Molina, Sentara Community Plan, and UnitedHealthcare Community Plan.
        </P>
        <P>For Zepbound, Wegovy, and Saxenda, you must have:</P>
        <UL>
          <li>
            BMI over 40, or BMI over 37 with high cholesterol, high blood pressure, or type 2
            diabetes; and
          </li>
          <li>
            Tried and failed a non-GLP-1 weight-loss medication, or be intolerant to all of them;
            and
          </li>
          <li>Tried and failed the plan&rsquo;s preferred product first, per the Preferred Drug List; and</li>
          <li>Not be taking another GLP-1 at the same time</li>
        </UL>
        <P>
          A <strong>&ldquo;failed trial&rdquo;</strong> is specifically defined. For older
          weight-loss drugs like phentermine, that means three months without losing 10 pounds. For
          orlistat, six months without losing 10 pounds. For a GLP-1, six months without a 5%
          body-weight reduction.
        </P>
        <P>
          <strong>Additional requirements.</strong> Your provider must attest in writing that your
          obesity is &ldquo;disabling and life threatening.&rdquo; You must be participating in
          nutritional counseling and a physical activity program. Your chart must include a
          weight-loss plan and accurate height and weight measurements from within the last 60
          days.
        </P>
        <P>
          <strong>Age minimums.</strong> Wegovy and Saxenda injections from age 12; Wegovy HD,
          Wegovy tablets, and Zepbound from age 18.
        </P>
        <P>
          <strong>How long approvals last.</strong> GLP-1 approvals run six months at a time.
          Renewal requires at least 5% weight loss compared with your most recent authorization and
          must be submitted within 30 days of expiration. Renewals stop once a member&rsquo;s BMI
          falls below 25.
        </P>
        <P>
          <strong>Oral GLP-1s face an extra hurdle.</strong> For a GLP-1 pill, you must also
          document a medical reason you can&rsquo;t use an injection, or show that two injectable
          products failed.
        </P>

        <H2 id="cova">Virginia state employee plans (COVA Care, COVA HealthAware, COVA HDHP)</H2>
        <Callout>
          <p>
            <strong>This changed on July 1, 2026, and the change was significant.</strong>
          </p>
          <p className="mt-3">
            Virginia&rsquo;s state employee health plan now requires a{" "}
            <strong>BMI of 35 or higher</strong> for weight-loss GLP-1 coverage &mdash; up from the
            previous 27/30 standard. The Department of Human Resource Management states it
            directly: &ldquo;A Body Mass Index (BMI) of 35 or higher is required to obtain a weight
            loss GLP-1 medication.&rdquo;
          </p>
          <p className="mt-3">
            <strong>Current users are not grandfathered.</strong> Existing patients become subject
            to the new BMI criteria once their current prior authorization expires.
          </p>
        </Callout>
        <P>
          <strong>Why it happened.</strong> In an April 2026 presentation to the Senate Finance and
          Appropriations Committee, DHRM reported that GLP-1 spending had grown from $30 million to
          $200 million, with Wegovy the plan&rsquo;s single largest drug expense at $47 million and
          Zepbound third at $34 million. Raising the BMI threshold was adopted as a cost-containment
          measure, described in plan documents as &ldquo;similar to Medicaid.&rdquo;
        </P>
        <P>
          <strong>What you&rsquo;ll pay.</strong> GLP-1s prescribed for weight loss sit on Tier 3
          for COVA Care &mdash; a $45 copay for up to a 34-day supply &mdash; and are not treated as
          specialty drugs. A new $150/$300 pharmacy deductible now applies to Tier 2, 3, and 4
          drugs, so your first fill of the plan year will cost more. COVA HealthAware and COVA HDHP
          members pay 20% coinsurance after their deductible. The plan also moved to the National
          Direct Preferred formulary on July 1, 2026, which may change which specific products are
          preferred.
        </P>

        <H3>If you no longer meet the new BMI threshold</H3>
        <P>
          The diagnosis backdoor matters most here. Sleep apnea, type 2 diabetes, established
          cardiovascular disease, and MASH liver disease are governed by different criteria than
          weight-loss coverage &mdash; see the{" "}
          <Link
            to="/medical-weight-loss/insurance"
            preload="intent"
            className="text-brand-deep underline hover:no-underline"
          >
            overview
          </Link>
          . If nothing applies, review{" "}
          <Link
            to="/medical-weight-loss/insurance/cash-pay-denials"
            className="text-brand-deep underline hover:no-underline"
          >
            denials and cash-pay pricing
          </Link>
          , or consider{" "}
          <Link to="/services" className="text-brand-deep underline hover:no-underline">
            surgical weight-loss options
          </Link>
          , which most Virginia plans still cover.
        </P>

        <H2 id="faqs">Frequently asked questions</H2>
        <FaqList faqs={FAQS} />

        <H2 id="more">Other coverage guides</H2>
        <SpokeCards exclude="/medical-weight-loss/insurance/medicaid-cova" />
      </PageSection>

      <InsuranceCTA />
    </>
  );
}
